import { fetchApi } from './UserApi';

export type LoginErrorCode =
	| 'invalid_credentials'
	| 'invalid_two_factor'
	| 'invalid_new_password'
	| 'rate_limited'
	| 'duplicate_email';
export type ResetPasswordErrorCode = 'invalid_token' | 'expired_token' | 'rate_limited';
export type RequestResetErrorCode = 'rate_limited' | 'duplicate_email';

export interface LoginClientSummary {
	id: number;
	name: string;
	markets?: string[];
	redirect: string;
}

export type LoginRedirectResponse = {
	redirect: string;
};

export type LoginErrorResponse = {
	error: true;
	code: LoginErrorCode;
};

export type LoginRequirementResponse = {
	two_factor_required: boolean;
	password_reset_required: boolean;
};

export type LoginResponse = LoginRedirectResponse | LoginErrorResponse | LoginRequirementResponse;

export type RequestPasswordResetSuccessResponse = {
	success: true;
};

export type RequestPasswordResetErrorResponse = {
	error: true;
	code: RequestResetErrorCode;
};

export type RequestPasswordResetResponse = RequestPasswordResetSuccessResponse | RequestPasswordResetErrorResponse;

export type ResetPasswordSuccessResponse = {
	success: true;
	redirect?: string;
};

export type ResetPasswordErrorResponse = {
	error: true;
	code: ResetPasswordErrorCode;
};

export type ResetPasswordResponse = ResetPasswordSuccessResponse | ResetPasswordErrorResponse;

export function isLoginError(response: LoginResponse): response is LoginErrorResponse {
	return 'error' in response;
}

export function isLoginRedirect(response: LoginResponse): response is LoginRedirectResponse {
	return 'redirect' in response;
}

export function isLoginRequirement(response: LoginResponse): response is LoginRequirementResponse {
	return 'two_factor_required' in response && 'password_reset_required' in response;
}

export type GetClientsErrorCode = 'not_authenticated' | 'rate_limited';

export type GetClientsSuccessResponse = {
	success: true;
	clients: LoginClientSummary[];
};

export type GetClientsErrorResponse = {
	error: true;
	code: GetClientsErrorCode;
};

export type GetClientsResponse = GetClientsSuccessResponse | GetClientsErrorResponse;

type PasswordByHashResponse =
	| {
			success: true;
	  }
	| {
			error: true;
	  };

type ChangePasswordErrorCode = 'invalid_credentials' | 'rate_limited' | 'not_authenticated' | 'invalid_password';

type ChangePasswordSuccessResponse = {
	success: true;
	redirect?: string;
};

type ChangePasswordErrorResponse = {
	error: true;
	code: ChangePasswordErrorCode;
};

export type ChangePasswordResponse = ChangePasswordSuccessResponse | ChangePasswordErrorResponse;

export default class LoginService {
	public static login(
		email: string,
		password: string,
		newPassword?: string,
		newPasswordConfirm?: string,
		authCode?: string,
	) {
		return fetchApi<LoginResponse>({
			baseUrl: '/',
			route: 'login/api/login',
			data: {
				email,
				password,
				new_password: newPassword,
				new_password_confirm: newPasswordConfirm,
				auth_code: authCode,
			},
			errorMessage: 'Unable to sign in right now.',
		}).then((res) => res.data);
	}

	public static requestPasswordReset(email: string) {
		return fetchApi<RequestPasswordResetResponse>({
			baseUrl: '/',
			route: 'login/api/request-password-reset',
			data: {
				email,
			},
			errorMessage: 'Unable to submit password reset request.',
		}).then((res) => res.data);
	}

	public static resetPassword(token: string, password: string) {
		return fetchApi<ResetPasswordResponse>({
			baseUrl: '/',
			route: 'login/api/reset-password',
			data: {
				token,
				password,
			},
			errorMessage: 'Unable to reset password.',
		}).then((res) => res.data);
	}

	public static validateResetToken(token: string) {
		return fetchApi<ResetPasswordResponse>({
			baseUrl: '/',
			route: 'login/api/validate-reset-token',
			data: {
				token,
			},
			errorMessage: 'Unable to validate reset link.',
		}).then((res) => res.data);
	}

	public static getClients() {
		return fetchApi<GetClientsResponse>({
			baseUrl: '/',
			route: 'login/api/get-clients',
			data: {},
			errorMessage: 'Unable to load your accounts.',
		}).then((res) => res.data);
	}

	public static requestPasswordByHash(hash: string) {
		return fetchApi<PasswordByHashResponse>({
			baseUrl: '/',
			route: 'login/api/request-password-by-hash',
			data: {
				hash,
			},
			errorMessage: 'Unable to send a password setup link.',
		}).then((res) => res.data);
	}
}
